import React from 'react';
import type { FormikValues } from 'formik';
import { getIn, useFormikContext } from 'formik';
import { isEmpty, isNil, trim } from 'lodash';

import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import { shouldShowFieldError } from '~/components/core/Formik/formikErrorUtils';
import StatusChipCore from '~/components/core/StatusChipCore';
import WarningCircleIcon from '~/components/icons/WarningCircle';
import type { FieldGroupSource } from '~/components/types/incident-configuration-types';

import copies from '../copies.json';

const TEXTS = copies.field_groups;

// Mirrors PLACEHOLDER_URL in server/modules/external_data_sources/internal/configuration_repository_manager.py:
// the address a configuration-repository promotion gives a source row it has to create, because the
// real URL and token are per-environment and do not travel with the configuration. The card's test
// reads the backend constant and fails when the two differ.
export const PLACEHOLDER_SOURCE_URL = 'https://placeholder.example.com/hierarchy';

// Typed against the source's own keys, so a key added to `FieldGroupSource` cannot reach the card
// without a label to report its errors under.
const SOURCE_FIELD_LABELS: Record<keyof FieldGroupSource, string> = {
  source_key: TEXTS.source_key_label,
  url: TEXTS.endpoint_url_label,
  auth_token: TEXTS.auth_token_label,
  static_json: TEXTS.static_json_label,
};

const isSourceField = (key: string): key is keyof FieldGroupSource => key in SOURCE_FIELD_LABELS;

const isErrorMap = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The host is what tells two endpoints apart at a glance; an address that does not parse is shown whole. */
const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const summarize = (source: FieldGroupSource): string => {
  if (!isNil(source.static_json)) {
    const { nodes } = source.static_json;
    const count = Array.isArray(nodes) ? nodes.length : 0;
    return count === 1
      ? TEXTS.source_summary_static_one
      : TEXTS.source_summary_static.replace('{count}', String(count));
  }
  const key = trim(source.source_key ?? '') || TEXTS.source_summary_no_key;
  const url = trim(source.url ?? '');
  return isEmpty(url)
    ? TEXTS.source_summary_endpoint.replace('{key}', key)
    : TEXTS.source_summary_endpoint_with_host.replace('{key}', key).replace('{host}', hostOf(url));
};

/**
 * What stops an endpoint source from answering, or `undefined` when nothing does. These are the gaps
 * a configuration-repository promotion leaves: it creates the source row with the placeholder URL,
 * and a key whose row does not exist comes back from the GET with no URL at all. Display only: this
 * changes nothing about what the save accepts.
 */
const notConfiguredReason = (source: FieldGroupSource): string | undefined => {
  if (!isNil(source.static_json)) {
    return undefined;
  }
  const url = trim(source.url ?? '');
  if (isEmpty(url)) {
    return TEXTS.source_not_configured_no_url;
  }
  if (url === PLACEHOLDER_SOURCE_URL) {
    return TEXTS.source_not_configured_placeholder_url;
  }
  return undefined;
};

/**
 * Whether an endpoint source has no token behind it. Not a gap: the schema accepts an unauthenticated
 * endpoint, so this is a hint rather than a "not configured" - but a promoted row never has a token,
 * so an admin setting one up has to be told.
 *
 * The GET hands back '*****' for a stored token and null for none, and an emptied field posts '' which
 * the server reads as "keep" - so an empty field still has a token behind it when the saved source
 * row came with one.
 */
const isMissingToken = (source: FieldGroupSource, savedAuthToken?: string | null): boolean =>
  isNil(source.static_json) && isEmpty(source.auth_token) && isEmpty(savedAuthToken);

interface FieldGroupSourceSummaryProps {
  /** Formik path of the group, whose `source` - and the schema's errors on it - are read beneath it. */
  fieldId: string;
  /** The token the saved source row was loaded with, which an emptied token field keeps. */
  savedAuthToken?: string | null;
  /** Labels the action VIEW SOURCE, because the dialog the card opens is then read-only. */
  isEditingDisabled: boolean;
  onOpen: () => void;
}

/**
 * The card's one line on the group's source, which is edited in a dialog: the kind and identity, what
 * the source is still missing, and the schema's errors on fields whose inputs are in the dialog.
 */
const FieldGroupSourceSummary: React.FC<FieldGroupSourceSummaryProps> = ({
  fieldId,
  savedAuthToken = undefined,
  isEditingDisabled,
  onOpen,
}) => {
  const { values, errors, touched, submitCount } = useFormikContext<FormikValues>();
  const sourcePath = `${fieldId}.source`;
  const source: FieldGroupSource = getIn(values, sourcePath) ?? {};
  const reason = notConfiguredReason(source);
  // A missing token is said under the summary like a reason is, but without the chip.
  const note = reason ?? (isMissingToken(source, savedAuthToken) ? TEXTS.source_not_configured_no_token : undefined);

  // Whatever the schema reported under `source`, shown by the rule the fields' own inputs use: once
  // touched - which applying the dialog does, for every field it hands back - or once a save has
  // been attempted.
  const sourceErrors: unknown = getIn(errors, sourcePath);
  const errorLines = isErrorMap(sourceErrors)
    ? Object.entries(sourceErrors).flatMap(([key, message]) =>
        typeof message === 'string' && shouldShowFieldError(`${sourcePath}.${key}`, touched, submitCount)
          ? [`${isSourceField(key) ? SOURCE_FIELD_LABELS[key] : key}: ${message}`]
          : []
      )
    : [];

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center justify-between gap-12">
        <div className="flex min-w-0 items-center gap-12">
          <Text variant={Text.VARIANTS.SM}>{summarize(source)}</Text>
          {reason ? <StatusChipCore status={TEXTS.source_not_configured} Icon={WarningCircleIcon} color="red" /> : null}
        </div>
        <Button variant="text" color="primary" onClick={onOpen}>
          {isEditingDisabled ? TEXTS.view_source : TEXTS.edit_source}
        </Button>
      </div>
      {note ? (
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {note}
        </Text>
      ) : null}
      {errorLines.map((line) => (
        <Text key={line} variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.ERROR}>
          {line}
        </Text>
      ))}
    </div>
  );
};

export default FieldGroupSourceSummary;
